import { UNIFIED_SOURCE, CLUSTER_IMG_SOURCE, DEFAULT_CLUSTER_MAX_ZOOM, CLUSTER_RADIUS } from '../utils/map-constants.js'
import { addAllLayers } from '../layers/layer-manager.js'
import { computeSpreads, applySpreads } from '../layers/overlap-spreader.js'

/**
 * Manages the unified GeoJSON source for MapLibre.
 *
 * Performance strategy (mirrors ARGOS):
 * - source.setData() is DEFERRED and coalesced — multiple setFeatures() calls
 *   within the same tick produce only ONE setData()
 * - source.updateData() for real-time position updates (incremental)
 * - 1-second coalescing for position + property updates
 *
 * Dos carriles de flush (merged al final por monitorId):
 *  - _pendingPositionUpdates: incluyen nueva geometria + speed/course/subtitle
 *    (lo que emite queuePositionUpdates y el path legacy de updateFeatures)
 *  - _pendingPropertyUpdates: solo propiedades, sin geometria. Usado cuando
 *    AnimationManager ya esta animando la feature: pushear TARGET geometry
 *    al coalesce hacia el final teletransportaria el icono adelante de la
 *    animacion, visible como un "salto hacia atras" cuando el siguiente rAF
 *    tick lo regresa al punto interpolado.
 */
// Coalesce window adaptativo por zoom level. En zoom bajo (cluster activo)
// los markers individuales no se ven — solo conteos de cluster. Una patrulla
// a 60 km/h se mueve ~166m en 10s, invisible cuando cluster bubble cubre
// 5-10 km. Por eso 10s es perfectamente aceptable visualmente y reduce
// drasticamente el costo de re-cluster (Supercluster reconstruye el arbol
// KD sobre 22K features en cada flush).
//
// En zoom alto (cluster desactivado, markers individuales visibles), volvemos
// a 1.5s para mantener la fluidez de la animacion del marker.
var COALESCE_WINDOW_LOW_ZOOM_MS = 10000
var COALESCE_WINDOW_HIGH_ZOOM_MS = 1500

export class SourceManager {
  constructor() {
    this.map = null
    this.features = new Map()  // monitorId → GeoJSON Feature
    this.initialized = false
    this._clusterMaxZoom = DEFAULT_CLUSTER_MAX_ZOOM

    // Single coalescing timer for ALL WS updates (positions + properties + entities)
    this._pendingPositionUpdates = []  // {monitorId, lng, lat, speed?, course?, subtitle?}
    this._pendingPropertyUpdates = []  // {monitorId, properties} — property-only (no geometry)
    // Indices monitorId → entry para deduplicar in-place: si llega un position
    // update para el mismo monitorId dentro de la ventana de coalesce, mergeamos
    // sobre el objeto existente en lugar de pushear uno nuevo. Antes generaba
    // ~1000 objetos {monitorId, lng, lat, speed, course, subtitle} cada 1.5s
    // que la mitad eran duplicados de devices que se mueven mas rapido que la
    // ventana — eso era el ~30% del GC churn observado en steady state.
    this._pendingPositionByMonitorId = new Map()
    this._pendingPropertyByMonitorId = new Map()
    this._coalesceTimer = null
    this._hasNewFeatures = false // true when features were ADDED (not just updated), requires setData

    // Pause flush coalesce mientras el usuario esta haciendo drag/zoom activo.
    // Setear en true en movestart, false en moveend. Mientras esta en true:
    //   - Position/property updates siguen llegando al pendingMap (in-memory)
    //   - El timer de coalesce NO se programa
    //   - En moveend, si hay updates pendientes, se hace flush inmediato
    // Beneficio: el drag queda libre de race conditions con cluster rebuild.
    // En procesadores lentos el drag se siente lento porque MapLibre tessela
    // tiles + Supercluster rebuild + paint compiten por CPU. Pausar el flush
    // durante el drag elimina el rebuild de la mezcla.
    this._isMoving = false

    // Deferred flush for setFeatures (initial load coalescing)
    this._fullFlushScheduled = false
  }

  init(map, options) {
    if (!options) options = {}
    var cluster = options.cluster !== undefined ? options.cluster : true
    var clusterMaxZoom = options.clusterMaxZoom || DEFAULT_CLUSTER_MAX_ZOOM
    this._clusterMaxZoom = clusterMaxZoom

    this.map = map

    // Pausa del flush coalesce durante movimiento activo del mapa. Los
    // listeners se attachan aqui (no en MapV2.vue) para que la pausa este
    // ligada al ciclo de vida del SourceManager. Si el SourceManager se
    // destruye, el destroy() los remove.
    var self = this
    this._onMoveStart = function() { self._isMoving = true }
    this._onMoveEnd = function() {
      self._isMoving = false
      // Flush inmediato si hay updates acumulados durante el move.
      var hasPositions = self._pendingPositionUpdates.length > 0
      var hasProperties = self._pendingPropertyUpdates.length > 0
      if (hasPositions || hasProperties) self._flushCoalesced()
    }
    map.on('movestart', this._onMoveStart)
    map.on('moveend', this._onMoveEnd)

    map.addSource(UNIFIED_SOURCE, {
      type: 'geojson',
      data: this._buildFeatureCollection(),
      // promoteId hace que MapLibre use feature.properties.monitorId
      // como identificador unico (string), evitando colisiones del int32
      // hashId con ~19K features. Sin esto, _applyDiffToSource rechaza
      // la FeatureCollection con "GeoJSON data is not compatible with
      // updateData" cuando hay 2 features con el mismo hash entero.
      promoteId: 'monitorId',
      cluster: cluster,
      clusterMaxZoom: clusterMaxZoom,
      clusterRadius: CLUSTER_RADIUS,
      // clusterMinPoints: 3 (default 2). Por default Supercluster forma
      // cluster con 2 puntos. Subir a 3 evita que pares dispersos formen
      // cluster falso, dejandolos como markers individuales — reduce
      // 10-20% del trabajo de cluster reduce() en cada rebuild.
      clusterMinPoints: 3,
      // buffer: 64 (default 128 px). Pixels de overflow al cortar tiles
      // para que features cerca del borde no se corten. Default 128 es
      // generoso para lineas/polygonos largos. Para markers (puntos),
      // 64 px es suficiente — reduce 3-8% del costo de splitTile en el
      // worker. Posible side effect: trails GPS muy largos cerca del
      // borde podrian cortarse — verificar visualmente con devices en
      // movimiento a zoom 12-14.
      buffer: 64,
      // maxzoom: 14 (default 18). El source no necesita generar tiles
      // mas alla de zoom 14 — a zoom > 14 el pipeline cambia a DOM
      // markers (no symbols clustered). Reducir el techo limita el
      // cache de tiles y baja loadTile calls del worker.
      maxzoom: 14,
      clusterProperties: this._buildClusterProperties(),
    })

    map.addSource(CLUSTER_IMG_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    this.initialized = true
  }

  /**
   * Bulk set features for an entity type.
   * The actual setData() is DEFERRED to the next microtask so that multiple
   * calls (e.g., 12 entity types in initial load) coalesce into ONE setData().
   */
  setFeatures(entityType, features) {
    // Remove old features of this type
    for (var entry of this.features) {
      if (entry[1].properties.entityType === entityType) {
        this.features.delete(entry[0])
      }
    }
    // Add new features
    for (var i = 0; i < features.length; i++) {
      this.features.set(features[i].properties.monitorId, features[i])
    }
    // Schedule a single deferred flush
    this._scheduleDeferredFlush()
  }

  /**
   * Queue incremental position updates. Flushed every 1 second via updateData().
   */
  queuePositionUpdates(updates) {
    for (var i = 0; i < updates.length; i++) {
      var update = updates[i]
      // Object pool / in-place merge: si ya hay un update pendiente para
      // este monitorId en la ventana de coalesce, mergeamos sobre el
      // objeto existente en vez de pushear uno nuevo. Reduce GC pressure
      // ~30% en steady state (10-15 menos MinorGC por minuto en operacion
      // con ~1000 devices movientes).
      var pending = this._pendingPositionByMonitorId.get(update.monitorId)
      if (pending) {
        pending.lng = update.lng
        pending.lat = update.lat
        if (update.speed != null) pending.speed = update.speed
        if (update.course != null) pending.course = update.course
        if (update.subtitle) pending.subtitle = update.subtitle
      } else {
        this._pendingPositionUpdates.push(update)
        this._pendingPositionByMonitorId.set(update.monitorId, update)
      }

      // Update in-memory feature
      var existing = this.features.get(update.monitorId)
      if (existing) {
        existing.geometry.coordinates = [update.lng, update.lat]
        if (update.speed != null) existing.properties.speed = update.speed
        if (update.course != null) existing.properties.course = update.course
        if (update.subtitle) existing.properties.subtitle = update.subtitle
      }
    }

    this._scheduleCoalescedFlush()
  }

  /**
   * Schedule a single coalesced flush (1.5s window).
   * Shared by position updates, property updates, and entity updates from WS.
   *
   * Window subido de 1000 → 1500ms: con 19K features, cada flush dispara
   * re-cluster + retesselación de tiles dentro de MapLibre, que en el
   * thread principal es el bloque dominante (~30%). Subir el window
   * reduce ~33% los flushes con un costo de UX nulo: la animación visible
   * de markers la maneja AnimationManager (interpolación rAF entre
   * old/new coords leídas de _monitors), no el setData del source.
   */
  _scheduleCoalescedFlush() {
    // Pause durante drag/zoom activo del mapa: en hardware lento el move
    // y el cluster rebuild compiten por CPU. Mientras _isMoving=true los
    // updates siguen entrando al pendingMap (in-memory) pero NO se programa
    // timer. En moveend se hace flush inmediato.
    if (this._isMoving) return
    if (this._coalesceTimer) return // already scheduled

    // Window adaptativo por zoom level. En zoom bajo (cluster activo) los
    // markers individuales no se ven — solo conteos del cluster bubble.
    // 10s de delay es invisible operativamente (patrulla a 60 km/h se mueve
    // 166m, dentro del area que cubre un cluster bubble) y reduce 6.7x los
    // rebuilds de Supercluster con 22K features. En zoom alto vuelven a
    // 1.5s para que la animacion del marker individual se vea fluida.
    var window = COALESCE_WINDOW_HIGH_ZOOM_MS
    if (this.map) {
      var z = this.map.getZoom()
      if (z < this._clusterMaxZoom) window = COALESCE_WINDOW_LOW_ZOOM_MS
    }
    var self = this
    this._coalesceTimer = setTimeout(function() {
      self._coalesceTimer = null
      self._flushCoalesced()
    }, window)
  }

  /**
   * Update features incrementally (for WS entity updates like buses:1, cameras:1).
   * Updates in-memory features and queues for the SAME 1s coalesced flush as positions.
   * NEVER calls setData() directly.
   */
  updateFeatures(updates) {
    for (var i = 0; i < updates.length; i++) {
      var update = updates[i]
      var id = update.properties.monitorId
      var existing = this.features.get(id)
      if (existing) {
        existing.geometry = update.geometry
        Object.assign(existing.properties, update.properties)
        // Existing feature updated — queue as position-style update for updateData()
        // In-place merge si ya hay un pending para este monitorId.
        var pending = this._pendingPositionByMonitorId.get(id)
        if (pending) {
          pending.lng = update.geometry.coordinates[0]
          pending.lat = update.geometry.coordinates[1]
          pending.speed = update.properties.speed || 0
          pending.course = update.properties.course || 0
          pending.subtitle = update.properties.subtitle || ''
        } else {
          var entry = {
            monitorId: id,
            lng: update.geometry.coordinates[0],
            lat: update.geometry.coordinates[1],
            speed: update.properties.speed || 0,
            course: update.properties.course || 0,
            subtitle: update.properties.subtitle || '',
          }
          this._pendingPositionUpdates.push(entry)
          this._pendingPositionByMonitorId.set(id, entry)
        }
      } else {
        // Truly NEW feature — needs setData() to add it to the source
        this.features.set(id, update)
        this._hasNewFeatures = true
      }
    }
    // Use the same 1s coalesce timer as positions
    this._scheduleCoalescedFlush()
  }

  /**
   * Update ONLY properties (status, icon, color, title, etc.) without touching
   * geometry. Usar cuando la geometria de la feature esta siendo manejada por
   * el AnimationManager: pushear la geometria TARGET por updateFeatures() hace
   * que el coalesce timer teletransporte al destino antes de que la animacion
   * termine, y el siguiente rAF tick regresa al punto interpolado — visible
   * como un "salto hacia atras" del icono.
   *
   * El merge final en _flushCoalesced combina property updates con position
   * updates si coinciden el mismo monitorId en la misma ventana.
   */
  updateFeatureProperties(updates) {
    for (var i = 0; i < updates.length; i++) {
      var update = updates[i]
      var id = update.properties.monitorId
      var existing = this.features.get(id)
      if (existing) {
        // Merge properties in-memory; geometry permanece como AnimationManager
        // (via updateFeatureCoords) la haya dejado con el TARGET.
        Object.assign(existing.properties, update.properties)
        // In-place merge para deduplicar property updates por monitorId
        // (mismo patron que queuePositionUpdates). Reduce GC churn.
        var pendingProp = this._pendingPropertyByMonitorId.get(id)
        if (pendingProp) {
          Object.assign(pendingProp.properties, update.properties)
        } else {
          var entry = { monitorId: id, properties: Object.assign({}, update.properties) }
          this._pendingPropertyUpdates.push(entry)
          this._pendingPropertyByMonitorId.set(id, entry)
        }
      } else {
        // New feature — necesita setData() para entrar a la source
        this.features.set(id, update)
        this._hasNewFeatures = true
      }
    }
    this._scheduleCoalescedFlush()
  }

  removeFeatures(monitorIds) {
    for (var i = 0; i < monitorIds.length; i++) {
      this.features.delete(monitorIds[i])
    }
    this._hasNewFeatures = true
    this._scheduleCoalescedFlush()
  }

  clearEntityType(entityType) {
    for (var entry of this.features) {
      if (entry[1].properties.entityType === entityType) {
        this.features.delete(entry[0])
      }
    }
    // Triggered by explicit user actions (layer toggle). Flush immediately
    // via microtask so the layer disappears on the next frame instead of
    // waiting for the 1s coalesce window, which is reserved for the WS
    // update stream (positions + entity diffs).
    this._scheduleDeferredFlush()
  }

  getAllFeatures() {
    return Array.from(this.features.values())
  }

  getFeaturesByType(entityType) {
    return Array.from(this.features.values())
      .filter(function(f) { return f.properties.entityType === entityType })
  }

  getFeature(monitorId) {
    return this.features.get(monitorId)
  }

  updateClusterSettings(enabled, maxZoom) {
    if (!this.map) return
    var source = this.map.getSource(UNIFIED_SOURCE)
    if (!source) return

    var data = this._buildFeatureCollection()

    // Must remove all layers using this source before removing the source
    var layersToRemove = []
    var map = this.map
    var allLayers = map.getStyle().layers || []
    for (var i = 0; i < allLayers.length; i++) {
      if (allLayers[i].source === UNIFIED_SOURCE || allLayers[i].source === CLUSTER_IMG_SOURCE) {
        layersToRemove.push(allLayers[i])
      }
    }
    for (var j = 0; j < layersToRemove.length; j++) {
      map.removeLayer(layersToRemove[j].id)
    }

    // Remove sources
    if (map.getSource(CLUSTER_IMG_SOURCE)) map.removeSource(CLUSTER_IMG_SOURCE)
    map.removeSource(UNIFIED_SOURCE)

    // Re-create with new cluster settings
    map.addSource(UNIFIED_SOURCE, {
      type: 'geojson',
      data: data,
      // Mantener consistencia con el init: ver comentario en init().
      promoteId: 'monitorId',
      cluster: enabled,
      clusterMaxZoom: maxZoom,
      clusterRadius: CLUSTER_RADIUS,
      clusterProperties: this._buildClusterProperties(),
    })
    map.addSource(CLUSTER_IMG_SOURCE, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    })

    // Re-add all layers
    addAllLayers(map)
  }

  /**
   * Update in-memory feature coordinates and properties WITHOUT flushing to MapLibre.
   * Used by AnimationManager: in-memory features hold the TARGET position so that any
   * full setData() (style switch, filter rebuild) writes the correct final coordinates.
   * The AnimationManager handles visual interpolation via its own updateData() calls.
   */
  updateFeatureCoords(monitorId, coords, props) {
    var existing = this.features.get(monitorId)
    if (!existing) return
    existing.geometry.coordinates = coords
    if (props) {
      if (props.speed != null) existing.properties.speed = props.speed
      if (props.course != null) existing.properties.course = props.course
      if (props.subtitle) existing.properties.subtitle = props.subtitle
    }
  }

  /**
   * Clean up timers. Call from component beforeDestroy.
   */
  destroy() {
    if (this._coalesceTimer) {
      clearTimeout(this._coalesceTimer)
      this._coalesceTimer = null
    }
    // Remove movestart/moveend listeners para no dejar referencias colgando
    // tras navegar fuera del mapa (mem leak).
    if (this.map) {
      if (this._onMoveStart) this.map.off('movestart', this._onMoveStart)
      if (this._onMoveEnd) this.map.off('moveend', this._onMoveEnd)
    }
    this._onMoveStart = null
    this._onMoveEnd = null
    this._isMoving = false
    this._pendingPositionUpdates = []
    this._pendingPropertyUpdates = []
    this._pendingPositionByMonitorId.clear()
    this._pendingPropertyByMonitorId.clear()
    this._fullFlushScheduled = false
  }

  // ─── Private ──────────────────────────────────────────────

  /**
   * Schedule a deferred full flush using microtask.
   * Multiple calls within the same synchronous execution coalesce into ONE setData().
   * This is the KEY fix: initial load (12 types) → 1 setData() instead of 12.
   */
  _scheduleDeferredFlush() {
    if (this._fullFlushScheduled) return
    this._fullFlushScheduled = true
    var self = this
    Promise.resolve().then(function() {
      self._fullFlushScheduled = false
      self._fullFlush()
    })
  }

  /**
   * Flush ALL coalesced updates (positions + properties + entities).
   * Uses updateData() when possible, falls back to setData() when new features were added.
   *
   * Merge rules (por monitorId):
   *  - Si hay position update: incluye newGeometry + speed/course/subtitle
   *  - Si hay property update encima: hace Object.assign sobre las props
   *    (status, icon, color, title, etc.) — la geometria no se toca si solo
   *    habia property update (feature animada por AnimationManager).
   */
  _flushCoalesced() {
    if (!this.initialized || !this.map) return

    var source = this.map.getSource(UNIFIED_SOURCE)
    if (!source) return

    // If truly NEW features were added (not just updates to existing), we need full setData
    if (this._hasNewFeatures) {
      this._hasNewFeatures = false
      this._pendingPositionUpdates = []
      this._pendingPropertyUpdates = []
      this._pendingPositionByMonitorId.clear()
      this._pendingPropertyByMonitorId.clear()
      source.setData(this._buildFeatureCollection())
      try { if (window.__bootProfile) window.__bootProfile.count('source.setData', 0) } catch (_) {}
      try { if (window.__memTracer) window.__memTracer.tick('sm.setData') } catch (_) {}
      return
    }

    if (this._pendingPositionUpdates.length === 0 && this._pendingPropertyUpdates.length === 0) return

    var latestByMonitor = {}

    // Position updates: geometry + 3 animated props (speed/course/subtitle)
    for (var i = 0; i < this._pendingPositionUpdates.length; i++) {
      var u = this._pendingPositionUpdates[i]
      var props = {}
      if (u.speed != null) props.speed = u.speed
      if (u.course != null) props.course = u.course
      if (u.subtitle) props.subtitle = u.subtitle
      latestByMonitor[u.monitorId] = {
        hasGeometry: true,
        lng: u.lng,
        lat: u.lat,
        properties: props,
      }
    }

    // Property-only updates: mergean encima (status, icon, color, title, etc.)
    // Si no habia position update para el mismo monitorId, el entry queda sin
    // geometry (property-only flush — no teletransporta features animadas).
    for (var j = 0; j < this._pendingPropertyUpdates.length; j++) {
      var p = this._pendingPropertyUpdates[j]
      var entry = latestByMonitor[p.monitorId]
      if (entry) {
        Object.assign(entry.properties, p.properties)
      } else {
        latestByMonitor[p.monitorId] = {
          hasGeometry: false,
          properties: Object.assign({}, p.properties),
        }
      }
    }

    var updatePayload = []
    var keys = Object.keys(latestByMonitor)
    for (var k = 0; k < keys.length; k++) {
      var monitorId = keys[k]
      // Descartar updates para features que ya NO estan en memoria (fueron
      // removidos por TTL, layer toggle, filter rebuild, etc). MapLibre
      // updateData exige que cada id del payload exista en el source; si
      // no, lanza "GeoJSON data is not compatible with updateData" y mata
      // el batch entero. Filtrando aqui silenciamos el caso de carrera
      // sin perder updates validos del mismo flush.
      if (!this.features.has(monitorId)) continue
      var merged = latestByMonitor[monitorId]
      var propsToUpdate = []
      for (var propKey in merged.properties) {
        var v = merged.properties[propKey]
        // MapLibre rechaza addOrUpdateProperties con valores undefined/null
        // o con NaN (toJSON los convierte en null y MapLibre lanza
        // "GeoJSON data is not compatible with updateData"). Saltamos esos
        // entries — la prop preexistente en el source queda intacta.
        if (v === undefined || v === null) continue
        if (typeof v === 'number' && isNaN(v)) continue
        propsToUpdate.push({ key: propKey, value: v })
      }
      // Si no hay geometria nueva NI props validas, no tiene sentido
      // emitir una entry vacia para este monitor.
      if (!merged.hasGeometry && propsToUpdate.length === 0) continue
      var payloadEntry = {
        // promoteId:'monitorId' en el source → MapLibre usa la string
        // como id. No hashear: con ~19K features el int32 colisionaba
        // y rechazaba el batch entero.
        id: monitorId,
      }
      // Omitir la key cuando vacia: MapLibre rechaza addOrUpdateProperties:[].
      // Mismo guard que animation-manager.js usa.
      if (propsToUpdate.length > 0) payloadEntry.addOrUpdateProperties = propsToUpdate
      if (merged.hasGeometry) {
        payloadEntry.newGeometry = { type: 'Point', coordinates: [merged.lng, merged.lat] }
      }
      updatePayload.push(payloadEntry)
    }

    this._pendingPositionUpdates = []
    this._pendingPropertyUpdates = []
    this._pendingPositionByMonitorId.clear()
    this._pendingPropertyByMonitorId.clear()

    if (updatePayload.length === 0) return

    try {
      source.updateData({ update: updatePayload })
      try { if (window.__bootProfile) window.__bootProfile.count('source.updateData', updatePayload.length) } catch (_) {}
      try { if (window.__memTracer) window.__memTracer.tick('sm.updateData') } catch (_) {}
    } catch (e) {
      // Fallback final: si MapLibre rechaza el batch igual (ej: el source
      // fue recreado entre el filter y el flush), reconstruir todo. Costoso
      // pero raro — solo en transiciones de estilo/cluster.
      source.setData(this._buildFeatureCollection())
      try { if (window.__bootProfile) window.__bootProfile.count('source.setData.fallback', 0) } catch (_) {}
      try { if (window.__memTracer) window.__memTracer.tick('sm.setData.fallback') } catch (_) {}
    }
  }

  /**
   * Full setData flush — expensive, only called via deferred schedule.
   */
  _fullFlush() {
    if (!this.initialized || !this.map) return
    var source = this.map.getSource(UNIFIED_SOURCE)
    if (source) {
      source.setData(this._buildFeatureCollection())
      try { if (window.__bootProfile) window.__bootProfile.count('source.setData.full', 0) } catch (_) {}
      try { if (window.__memTracer) window.__memTracer.tick('sm.fullFlush') } catch (_) {}
    }
    // setData ya incluye el estado actual de todas las features — descartamos
    // las colas pendientes para que no re-flusheen datos ya emitidos.
    this._pendingPositionUpdates = []
    this._pendingPropertyUpdates = []
    this._pendingPositionByMonitorId.clear()
    this._pendingPropertyByMonitorId.clear()
    if (this._coalesceTimer) {
      clearTimeout(this._coalesceTimer)
      this._coalesceTimer = null
    }
  }

  _buildFeatureCollection() {
    // Recalcular y aplicar offsets de spread radial para incidentes que
    // comparten coord exacta. Es O(N) con N = total features (~19K en
    // produccion); 1 pasada para construir el index de coords + 1 pasada
    // para asignar offsets. Se invoca en todos los paths de full flush
    // (_fullFlush, _flushCoalesced con _hasNewFeatures, init y
    // updateClusterSettings) — basta con esto porque incidentes no se
    // mueven (geometria estable), entonces la membresia del grupo solo
    // cambia cuando se agregan o remueven features.
    var spreads = computeSpreads(this.features.values())
    applySpreads(this.features.values(), spreads)
    return {
      type: 'FeatureCollection',
      features: Array.from(this.features.values()),
    }
  }

  _buildClusterProperties() {
    var props = {}
    var types = [
      'device', 'bus', 'camera', 'lpr', 'incident', 'sensor',
      'external_sensor', 'external_device', 'presa', 'sismo', 'fire', 'cpr',
      'reportant', 'sos_call', 'route_lpr',
    ]
    for (var i = 0; i < types.length; i++) {
      var t = types[i]
      props[t + '_count'] = ['+', ['case', ['==', ['get', 'entityType'], t], 1, 0]]
    }
    // MARKERS-DESIGN-SPEC.md §7: indicador de alerta en cluster card.
    // Contamos features con alertLevel ∈ {base, high, medium} (NO low, que
    // no tiene halo) y separamos el sub-count de SOS para elegir color
    // del indicador (alert-sos tiene prioridad sobre alert-danger).
    props.alert_count = [
      '+',
      ['case',
        ['match', ['get', 'alertLevel'], ['base', 'high', 'medium'], true, false],
        1, 0,
      ],
    ]
    props.sos_alert_count = [
      '+',
      ['case',
        ['all',
          ['==', ['get', 'entityType'], 'sos_call'],
          ['match', ['get', 'alertLevel'], ['base', 'high', 'medium'], true, false],
        ],
        1, 0,
      ],
    ]
    return props
  }
}
